import { ref, computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
const { flux, fluxDuration } = require('@influxdata/influxdb-client')
import {
	INFLUXDB,
	queryApi,
	parseTimeDate,
} from '@/api/influxdb.js'
import { useAccontStore } from '@/stores/AccountStore.js'
import { useStationsPassport } from './stationsPassport.js'
import {
	dateTimeToRFC3339,
	calculateTimeWindows,
} from '@/helpers.js'
import { Station } from '@/models/station.js'




export const metricSchema = Object.freeze(Object.assign({
	metricId: Object.freeze({
		key: 'metricId'
	}),

	stationId: Object.freeze({
		key: 'stationId'
	}),

	time: Object.freeze({
		key: 'time'
	}),

	unitId: Object.freeze({
		key: 'unitId'
	}),

	value: Object.freeze({
		key: 'value'
	}),
}))




export function useMetricHistory() {
	const accountStore = useAccontStore()

	const {
		passportsMap,
	} = useStationsPassport(
		accountStore.isPrimary ? null : accountStore.account[accountStore.schema.organizationId],
		accountStore.isPrimary ? null : accountStore.departmentsScope,
		accountStore.isPrimary ? null : accountStore.sectorsScope,
	)

	// Reactive lower bound for alerts fetch time window.
	const fromTime = ref(null)
	// Reactive upper bound for alerts fetch time window.
	const toTime = ref(null)
	// Metrics ids Array
	const metricsId = ref(null)
	// Agregate window or read every metric vlue
	const isAggregateWindow = ref(null)
	// Get only unique values (can`t agregate window and get unique)
	const isOnlyUniqueMetrics = ref(null)
	// Station id filter - array of filtered station ids
	// const stationIds = ref([])

	const queryFrom = computed(() =>
		fromTime.value instanceof Date
			? dateTimeToRFC3339(fromTime.value)
			: null
	)

	const queryTo = computed(() =>
		toTime.value instanceof Date
			? dateTimeToRFC3339(toTime.value)
			: fluxDuration('-1s')
	)

	// Map for all stations {metricsId: stationId}
	const stationsIdMap = computed(() => {
		if (!(passportsMap.value instanceof Map)) {
			return new Map()
		}

		return new Map(Array.from(passportsMap.value.values()).map(
			(passport) => new Array(passport[Station.passportKeys.metricsId], passport[Station.passportKeys.id])
		))
		// return new Map([['31', '6512872ae29911dbc0e283fe'], ]) for test
	})

	const queryStationsId = computed(() => {
		return Array.from(stationsIdMap.value.keys())
	})

	const metricQuery = useQuery({
		// Include auth identity and time filter in key to auto-refetch on change
		queryKey: computed(() => [
			'metrics',
			...(metricsId.value == null ? [] : metricsId.value),
			queryFrom.value,
			queryTo.value,
			...queryStationsId.value,
			isOnlyUniqueMetrics.value,
			isAggregateWindow.value,

		]),
		enabled: computed(() =>
			fromTime.value !== null
			&& toTime.value !== null
			&& queryStationsId.value.length > 0
			&& metricsId.value !== null
		),
		queryFn: async () => {
			// console.log('fetch metrics history', metricsId.value == null ? [] : metricsId.value.length, queryFrom.value, queryTo.value, queryStationsId.value.length)
			// Fetch like readStationMetrics() in influxdb.js
			let period = calculateTimeWindows(fromTime.value, toTime.value).influxDbPeriod

			let query = flux`
			import "types"

			station_ids_arr = ${queryStationsId.value}
			metric_ids_arr = ${metricsId.value}

			common = from(bucket: ${INFLUXDB.station_metrics.bucket})
				|> range(start: ` + queryFrom.value + flux`, stop: ` + queryTo.value + flux`)
			`
			

			// Filter only interested metrics
			//|> filter(fn: (r) => contains(value: r._field, set: metric_ids_arr))
			query += flux`
			|> filter(fn: (r) => r["_field"] == "${metricsId.value[0]}"`
			for (var j = 1; j < metricsId.value.length; j++) {
				query += flux` or r["_field"] == "${metricsId.value[j]}"`
			}
			query += flux`)`

			// Filter only interested stations
			query += flux`|> filter(fn: (r) => contains(value: r["station_id"], set: station_ids_arr))`

			if (isOnlyUniqueMetrics.value !== null && isOnlyUniqueMetrics.value == true) {
				// Aggregate unique metrics
				query += flux`
				common
					|> duplicate(column: "_value", as: "_value_original")
					|> filter(fn: (r) =>
						types.isType(v: r._value, type: "bool") or
						types.isType(v: r._value, type: "int") or
						types.isType(v: r._value, type: "uint") or
						types.isType(v: r._value, type: "float")
					)
					|> map(fn: (r) => ({
						r with
						_cmp: if types.isType(v: r._value, type: "bool")
							then (if r._value then 1.0 else 0.0)
							else float(v: r._value)
					}))
					|> duplicate(column: "_cmp", as: "prev_diff")
					|> sort(columns: ["_time"], desc: false)
					|> difference(columns: ["prev_diff"])
					|> filter(fn: (r) => r.prev_diff != 0.0)
					|> map(fn: (r) => ({ r with _value: r._value_original }))
					|> drop(columns: ["_cmp", "prev_diff", "_value_original"])
					|> set(key: "value_function", value: "unique")
				`
			} else {
// Not tested
				if (isAggregateWindow.value !== null && isAggregateWindow.value == true) {
					query += flux`
					isNumeric = (v) =>
						types.isType(v: v, type: "int") or
						types.isType(v: v, type: "uint") or
						types.isType(v: v, type: "float")

					numeric = common |> filter(fn: (r) => isNumeric(v: r._value))
					boolean = common |> filter(fn: (r) => types.isType(v: r._value, type: "bool"))

					medianMetrics = numeric
						|> aggregateWindow(every: `+ period + flux`, fn: median, createEmpty: false)
						|> set(key: "value_function", value: "median")

					minMetrics = numeric
						|> aggregateWindow(every: `+ period + flux`, fn: min, createEmpty: false)
						|> set(key: "value_function", value: "min")

					maxMetrics = numeric
						|> aggregateWindow(every: `+ period + flux`, fn: max, createEmpty: false)
						|> set(key: "value_function", value: "max")

					boolMetrics = boolean
						|> aggregateWindow(every: ` + period + flux`, fn: last, createEmpty: false)
						|> set(key: "value_function", value: "bool_last")

					union(tables:[medianMetrics, minMetrics, maxMetrics, boolMetrics])
					`
				}
			}

			const rows = await queryApi.collectRows(query)

			return rows.map((row) => {
				return {
					unitId: row._measurement,
					value: row._value,
					metricId: row._field,
					stationId: stationsIdMap.value.get(row.station_id),
					time: parseTimeDate(row._time),
				}
			})
		},
		// Keep results warm to reduce repeated network requests.
		staleTime: 10 * 60 * 1000,
	})

	const history = computed(() => metricQuery.data.value ?? [])

	// Set period or alert type filters
	function setFilters(
		filters={
			fromTime: null,
			toTime: null,
			metricsId: null,
			isAggregateWindow: null,
			isOnlyUniqueMetrics: null,
		}
	) {
		// console.log('setFilters', filters)
		if (filters.fromTime !== null && filters.fromTime instanceof Date) {
			fromTime.value = new Date(filters.fromTime)
		}

		if (filters.toTime !== null && filters.toTime instanceof Date) {
			toTime.value = new Date(filters.toTime)
		}

		if (filters.metricsId != null && Array.isArray(filters.metricsId)) {
			metricsId.value = filters.metricsId
		}

		if (filters.isAggregateWindow != null && typeof filters.isAggregateWindow === 'boolean') {
			isAggregateWindow.value = filters.isAggregateWindow
		}

		if (filters.isOnlyUniqueMetrics != null && typeof filters.isOnlyUniqueMetrics === 'boolean') {
			isOnlyUniqueMetrics.value = filters.isOnlyUniqueMetrics
		}
	}

	return {
		history,

		isLoading: metricQuery.isLoading,
		isError: metricQuery.isError,
		error: metricQuery.error,

		setFilters,
	}
}